import { Alert, Button, Input, Space } from 'antd'
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { useState, useMemo } from 'react'
import { AppLayout } from '../components/AppLayout'
import { ZealysLoginsTable } from '../features/internal-access/components/ZealysLoginsTable'
import { GrantAccessModal } from '../features/internal-access/components/GrantAccessModal'
import { LoginAccessDrawer } from '../features/internal-access/components/LoginAccessDrawer'
import { useZealysLogins } from '../features/internal-access/hooks/useZealysLogins'
import { Loading } from '../components/Loading'
import { EmptyState } from '../components/EmptyState'
import type { ZealysLogin } from '../features/internal-access/types/internal-access.types'
import '../features/internal-access/components/ZealysLoginsTable.css'

export function InternalAccessPage() {
  const { logins, loading, error, refresh } = useZealysLogins()
  const [selectedLoginId, setSelectedLoginId] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [grantAccessLogin, setGrantAccessLogin] = useState<ZealysLogin | null>(null)

  const filteredLogins = useMemo(() => {
    if (!searchTerm.trim()) return logins

    const search = searchTerm.toLowerCase().trim()
    return logins.filter(
      login =>
        login.loginCode.toLowerCase().includes(search) ||
        login.loginName?.toLowerCase().includes(search) ||
        login.email.toLowerCase().includes(search)
    )
  }, [logins, searchTerm])

  const selectedLogin = useMemo(() => {
    return logins.find(login => login.id === selectedLoginId) || null
  }, [logins, selectedLoginId])

  const handleGrantAccessFromDrawer = () => {
    if (selectedLogin) {
      setGrantAccessLogin(selectedLogin)
    }
  }

  if (error) {
    return (
      <AppLayout>
        <Alert
          message="Error Loading Logins"
          description={error.message}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={refresh}>
              Retry
            </Button>
          }
        />
      </AppLayout>
    )
  }

  if (loading && logins.length === 0) {
    return (
      <AppLayout>
        <Loading />
      </AppLayout>
    )
  }

  if (!loading && logins.length === 0) {
    return (
      <AppLayout>
        <EmptyState description="No Zealys logins found" />
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <div style={{ display: 'flex', gap: '16px', height: 'calc(100vh - 112px)' }}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0
          }}
        >
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              flexShrink: 0
            }}
          >
            <Space size="middle">
              <Input
                placeholder="Search by login code, name or email..."
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ width: 350 }}
                allowClear
              />
              <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
                Refresh
              </Button>
            </Space>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            {filteredLogins.length === 0 && searchTerm ? (
              <EmptyState description={`No logins found matching "${searchTerm}"`} />
            ) : (
              <ZealysLoginsTable
                logins={filteredLogins}
                loading={loading}
                selectedLoginId={selectedLoginId}
                onRowClick={setSelectedLoginId}
              />
            )}
          </div>
        </div>

        {selectedLoginId && (
          <div
            style={{
              width: '500px',
              flexShrink: 0,
              borderLeft: '1px solid #d9d9d9',
              overflow: 'auto',
              backgroundColor: '#ffffff',
              borderRadius: '8px'
            }}
          >
            <LoginAccessDrawer
              login={selectedLogin}
              onClose={() => setSelectedLoginId(null)}
              onGrantAccess={handleGrantAccessFromDrawer}
              onRefresh={refresh}
            />
          </div>
        )}
      </div>

      <GrantAccessModal
        open={!!grantAccessLogin}
        login={grantAccessLogin}
        onClose={() => setGrantAccessLogin(null)}
        onSuccess={() => {
          refresh()
          setGrantAccessLogin(null)
        }}
      />
    </AppLayout>
  )
}
